import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const emptyCustomer = {
    code: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    payment_terms: "",
};

export function Customers() {
    const [rows, setRows] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyCustomer);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get("/customers");
        setRows(data);
    };

    useEffect(() => {
        load();
    }, []);

    const updateForm = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const resetForm = () => {
        setForm(emptyCustomer);
        setShowCreate(false);
    };

    const createCustomer = async () => {
        const code = form.code.trim().toUpperCase();
        const name = form.name.trim();

        if (!code) {
            toast.error("Customer code is required.");
            return;
        }

        if (!name) {
            toast.error("Customer name is required.");
            return;
        }

        setSaving(true);

        try {
            await api.post("/customers", {
                code,
                name,
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
                address: form.address.trim() || null,
                payment_terms: form.payment_terms.trim() || null,
            });

            toast.success(`Customer ${code} created in SAP`);
            resetForm();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="customers-page">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Master Data</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Customers</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Customer master list synced with SAP Business Partners.
                    </p>
                </div>

                <button
                    onClick={() => setShowCreate(true)}
                    className="h-9 px-4 rounded bg-foreground text-background text-sm"
                    data-testid="customer-add-button"
                >
                    Add Customer
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <SummaryCard label="Customers" value={rows.length.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <SummaryCard label="SAP Created" value={rows.filter((c) => c.sap_status === "created_in_sap").length.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <SummaryCard label="With Email" value={rows.filter((c) => c.email).length.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <SummaryCard label="With Phone" value={rows.filter((c) => c.phone).length.toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1000px]">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Address</th>
                                <th>Terms</th>
                                <th>SAP Status</th>
                                <th className="text-right">Credit limit</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                        No customers found.
                                    </td>
                                </tr>
                            )}

                            {rows.map((c) => (
                                <tr key={c.customer_code}>
                                    <td className="font-mono">{c.customer_code}</td>
                                    <td>{c.name}</td>
                                    <td className="text-muted-foreground">{c.email || "—"}</td>
                                    <td className="font-mono">{c.phone || "—"}</td>
                                    <td className="text-muted-foreground">{c.address || "—"}</td>
                                    <td>{c.payment_terms || "—"}</td>
                                    <td>
                                        <span className="text-xs rounded border border-border px-2 py-1">
                                            {c.sap_status || "synced"}
                                        </span>
                                    </td>
                                    <td className="sc-num">{Number(c.credit_limit || 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
                    <div className="sc-card p-5 w-full max-w-2xl space-y-4 bg-background" onClick={(e) => e.stopPropagation()} data-testid="customer-create-modal">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Create Customer</div>
                            <div className="text-lg font-semibold">New SAP Business Partner</div>
                            <p className="text-sm text-muted-foreground mt-1">
                                This will create the customer directly in SAP OCRD and immediately add it to Sale Hub.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Customer Code" required>
                                <input
                                    className="sc-input uppercase"
                                    value={form.code}
                                    maxLength={15}
                                    placeholder="e.g. CUST001"
                                    onChange={(e) => updateForm("code", e.target.value.toUpperCase())}
                                    data-testid="customer-code"
                                />
                            </Field>

                            <Field label="Customer Name" required>
                                <input
                                    className="sc-input"
                                    value={form.name}
                                    placeholder="Customer / Company name"
                                    onChange={(e) => updateForm("name", e.target.value)}
                                    data-testid="customer-name"
                                />
                            </Field>

                            <Field label="Email">
                                <input
                                    className="sc-input"
                                    type="email"
                                    value={form.email}
                                    placeholder="customer@example.com"
                                    onChange={(e) => updateForm("email", e.target.value)}
                                    data-testid="customer-email"
                                />
                            </Field>

                            <Field label="Phone">
                                <input
                                    className="sc-input"
                                    value={form.phone}
                                    placeholder="09171234567"
                                    onChange={(e) => updateForm("phone", e.target.value)}
                                    data-testid="customer-phone"
                                />
                            </Field>

                            <Field label="Payment Terms">
                                <input
                                    className="sc-input"
                                    value={form.payment_terms}
                                    placeholder="Optional"
                                    onChange={(e) => updateForm("payment_terms", e.target.value)}
                                    data-testid="customer-payment-terms"
                                />
                            </Field>

                            <Field label="Address">
                                <input
                                    className="sc-input"
                                    value={form.address}
                                    placeholder="Billing address"
                                    onChange={(e) => updateForm("address", e.target.value)}
                                    data-testid="customer-address"
                                />
                            </Field>
                        </div>

                        <div className="rounded border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
                            Customer code must be unique and 15 characters or less. SAP will reject duplicate CardCodes.
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={resetForm} className="h-9 px-3 rounded border border-border text-sm">
                                Cancel
                            </button>
                            <button
                                onClick={createCustomer}
                                disabled={saving}
                                className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50"
                                data-testid="customer-save"
                            >
                                {saving ? "Creating…" : "Create in SAP"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function Products() {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        (async () => setRows((await api.get("/products")).data))();
    }, []);

    return (
        <div className="p-6 space-y-4" data-testid="products-page">
            <div>
                <div className="sc-section-title">Master Data</div>
                <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[800px]">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>Group</th>
                                <th>UoM</th>
                                <th className="text-right">List price</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-8 text-muted-foreground">
                                        No products found.
                                    </td>
                                </tr>
                            )}

                            {rows.map((p) => (
                                <tr key={p.item_code}>
                                    <td className="font-mono">{p.item_code}</td>
                                    <td>{p.item_name}</td>
                                    <td>{p.item_group}</td>
                                    <td>{p.uom}</td>
                                    <td className="sc-num">{Number(p.list_price || 0).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold mt-1">{value}</div>
        </div>
    );
}

function Field({ label, required = false, children }) {
    return (
        <label className="space-y-1">
            <div className="text-xs font-medium">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </div>
            {children}
        </label>
    );
}
